import {
  DataGrid,
  EmptyState,
  Icon,
  Spinner,
  theme,
  IconDictionary,
  TabSegmented,
  TabSegmentedGroup,
} from '@ketch-com/deck'
import {
  EventsBySystemDetail,
  Classification,
  ClassificationType,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { Box, Typography } from '@mui/material'
import { useEventsBySystem } from 'api/dataSentry/queries/useDataSentryEventsBySystem'
import { subDays } from 'date-fns'
import format from 'date-fns/format'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { useMemo, useRef, useState } from 'react'
import { getSpectrum } from 'pages/dataSentry/data/utils/DataSentryEventsDataGridUtil'
import { useTableFilters } from 'pages/dataSentry/hooks/useTableFilters'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { dataSentrySystemsDataGridUtil, DataSentrySystemsRowItem } from './utils/DataSentrySystemsDataGridUtil'
import { MaybeNull } from 'interfaces'
import {
  AuthorizationStatus as SystemsAuthorizationStatus,
  ControlStatus as SystemsControlStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'

export const DataSentrySystemsTable: React.FC = () => {
  const [systemsAuthorizationStatus, setSystemsAuthorizationStatus] = useState<SystemsAuthorizationStatus>(
    SystemsAuthorizationStatus.AUTHORIZED,
  )

  const [systemsControlStatus, setSystemsControlStatus] = useState<SystemsControlStatus>(
    SystemsControlStatus.UNSPECIFIED,
  )
  const { searchQuery, setSearchQuery, paginationModel, setPaginationModel, debouncedSearchQuery } = useTableFilters()
  const {
    selectedTimeRange,
    selectedPropertyCode,
    dataTypesTotalCount,
    hasPropertyAndScanTimeRange,
    setSystemDetails,
  } = useDataSentryContext()
  const { data: tableData, isLoading } = useEventsBySystem({
    params: {
      'start-date': selectedTimeRange?.startDate || format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      'end-date': selectedTimeRange?.endDate || format(new Date(), 'yyyy-MM-dd'),
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'authorization-status': systemsAuthorizationStatus,
      'control-status': systemsControlStatus,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const rowCountRef = useRef(tableData.total || 0)
  const rowCount = useMemo(() => {
    if (tableData.total !== undefined) {
      rowCountRef.current = tableData.total
    }
    return rowCountRef.current
  }, [tableData])

  const gridData: MaybeNull<DataSentrySystemsRowItem[]> = useMemo(() => {
    if (!tableData?.dataSystems?.length) return null

    return tableData.dataSystems.map((system: EventsBySystemDetail) => {
      const categories =
        system.classificationsByType
          ?.find(cls => cls.classificationType === ClassificationType.DATA_CATEGORY)
          ?.classifications?.map((c: Classification) => c?.name || '') || []

      const dataTypes =
        system.classificationsByType?.find(cls => cls.classificationType === ClassificationType.DATA_TYPE)
          ?.classifications || []

      const dataTypeCount = dataTypes.length

      let systemCount = system.count || 0
      const collectionEvents = systemCount.toString()

      const spectrum = getSpectrum(dataTypeCount, dataTypesTotalCount || 0)

      return {
        id: system.dataSystem?.code,
        name: system.dataSystem?.name || '',
        code: system.dataSystem?.code,
        logo: system.dataSystem?.logoUrl || '',
        trackers: system.trackers,
        trackerCount: system.trackerCount,
        collectionEvents,
        dataCategories: categories,
        collectionSpectrum: spectrum,
        usheredBy: system.usheredBy,
        system: system,
      }
    })
  }, [tableData, dataTypesTotalCount])

  const columns = useMemo(() => dataSentrySystemsDataGridUtil(setSystemDetails), [setSystemDetails])
  const isLoadingTableData = isLoading
  const displayDataGridEmptyState = !isLoadingTableData && !gridData && !debouncedSearchQuery
  const displayDataGrid = !isLoadingTableData && (gridData || (!gridData && debouncedSearchQuery))

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ backgroundColor: theme.palette.Common.Container }}
      borderRadius="11px"
      padding="16px 20px"
    >
      <Box>
        <Typography variant="h4">Systems</Typography>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <SearchTextInput
          onChange={nextValue => {
            setSearchQuery(nextValue)
          }}
          value={searchQuery}
          showClearButton
          minWidth="220px"
          sx={{
            width: '220px',
            '& .MuiInputBase-input': { maxWidth: '156px', minWidth: '156px' },
          }}
        />

        <Box display="flex" alignItems="center" gap={1}>
          {systemsAuthorizationStatus === SystemsAuthorizationStatus.AUTHORIZED ? (
            <TabSegmentedGroup
              size="small"
              value={systemsControlStatus}
              exclusive
              onChange={(_, value) => {
                setSystemsControlStatus?.(value)
                setPaginationModel(prev => ({ ...prev, page: 0 }))
              }}
            >
              <TabSegmented value={SystemsControlStatus.UNSPECIFIED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Overall</Typography>
                </Box>
              </TabSegmented>
              <TabSegmented value={SystemsControlStatus.CONTROLLED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Controlled</Typography>
                </Box>
              </TabSegmented>
              <TabSegmented value={SystemsControlStatus.UNCONTROLLED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Uncontrolled</Typography>
                </Box>
              </TabSegmented>
            </TabSegmentedGroup>
          ) : null}

          <TabSegmentedGroup
            size="small"
            value={systemsAuthorizationStatus}
            exclusive
            onChange={(_, value) => {
              setSystemsAuthorizationStatus(value)
              setPaginationModel(prev => ({ ...prev, page: 0 }))
            }}
          >
            <TabSegmented value={SystemsAuthorizationStatus.AUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Authorized</Typography>
              </Box>
            </TabSegmented>
            <TabSegmented value={SystemsAuthorizationStatus.UNAUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Unauthorized</Typography>
              </Box>
            </TabSegmented>
          </TabSegmentedGroup>
        </Box>
      </Box>

      {isLoadingTableData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : null}

      {displayDataGridEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={380} pl="10px" pr="10px" pb="10px">
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={
              <Icon name={IconDictionary.OTableView} width="48px" height="48px" iconColor={theme.palette.Black.o48} />
            }
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {displayDataGrid ? (
        <DataGrid
          sx={{
            border: 0,
            paddingX: 0,
            maxHeight: '620px',
            height: '620px',
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
              backgroundImage: 'unset',
              cursor: 'auto',
            },
            ...(isLoadingTableData
              ? {
                  '& .MuiDataGrid-virtualScroller': {
                    height: '300px',
                  },
                }
              : {}),
          }}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableRowHoverStates
          disableChildrenSorting={false}
          getRowHeight={() => 'auto'}
          getRowId={row => row.id}
          rowCount={rowCount}
          rows={gridData || []}
          columns={columns}
          pagination
          pageSizeOptions={[5, 10, 20]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={isLoadingTableData}
          paginationMode="server"
          autosizeOnMount
          autosizeOptions={{
            expand: false,
            includeHeaders: true,
            includeOutliers: false,
          }}
          noRowsOverlayProps={{ buttonTitle: '' }}
        />
      ) : null}
    </Box>
  )
}
